import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';

import { getENV, useOnClickOutside } from '@libs/common-next';
import type { Suggestions } from '@libs/experian';
import { getFormattedAddress, getStreetAddressData } from '@libs/experian/server';

type UseStreetAddressInputProps = {
    value: string;
    onAddressSelect: (address: { street: string; city: string; state: string; zip: string }) => void;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

export function useStreetAddressInput({ value, onAddressSelect, onChange }: UseStreetAddressInputProps) {
    const [suggestions, setSuggestions] = useState<Suggestions[]>([]);
    const [inputValue, setInputValue] = useState(value);
    const selectedAddress = useRef('');
    const containerRef = useRef<HTMLDivElement>(null);

    const clearSuggestions = useCallback(() => {
        setSuggestions([]);
    }, []);

    useOnClickOutside(containerRef, clearSuggestions);

    const [, cancel] = useDebounce(
        async () => {
            if (selectedAddress.current === inputValue || inputValue.length === 0 || inputValue.length < 2) {
                setSuggestions([]);
                return;
            }

            const data = await getStreetAddressData(inputValue);

            if (data?.result?.suggestions) {
                setSuggestions(data.result.suggestions);
            }
        },
        400,
        [inputValue]
    );

    useEffect(() => {
        cancel();
    }, []);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const handleInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value;
            setInputValue(newValue);
            onChange?.(event);
        },
        [onChange, cancel]
    );

    const handleAddressSelect = useCallback(
        async (streetAddressKey: string) => {
            setSuggestions([]);
            const addressResponse = await getFormattedAddress(streetAddressKey);

            if (!addressResponse) {
                return;
            }

            const { address } = addressResponse.result;
            const street = address.address_line_1 + address.address_line_2 + address.address_line_3;

            let zip = address.postal_code;
            if (getENV('NEXT_PUBLIC_LOCALE') === 'en-US') {
                const dashIndex = address.postal_code.indexOf('-');
                zip = dashIndex !== -1 ? address.postal_code.substring(0, dashIndex) : address.postal_code;
            }

            const state = address.region;
            const city = address.locality;
            selectedAddress.current = street;
            onAddressSelect({ street, city, state, zip });
        },
        [onAddressSelect]
    );

    return {
        inputValue,
        suggestions,
        containerRef,
        handleInputChange,
        handleAddressSelect,
    };
}
